// src/components/seo/HomeSEO.tsx

import { Helmet } from 'react-helmet-async';

export default function HomeSEO() {
  const siteUrl = 'https://bookoll.com';

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Bookoll',
    url: siteUrl,
    description:
      'Software de reservas online y gestión de agenda para negocios de servicios. Gestiona citas, clientes y trabajadores con Bookoll.',
    inLanguage: 'es-ES',
    publisher: { '@id': `${siteUrl}/#organization` },
    // [4] URL corregida: la app usa /explorar?q= no /?q=
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/explorar?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Bookoll',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png',
      width: 200,
      height: 60,
    },
    sameAs: [
      siteUrl,
      'https://instagram.com/bookoll',
      'https://linkedin.com/company/bookoll',
      // Añadir cuando existan:
      // 'https://twitter.com/bookoll',
      // 'https://facebook.com/bookoll',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+34-662-012-573',
      email: 'info@bookoll.com',
      contactType: 'customer support',
      url: `${siteUrl}/contact`,
      availableLanguage: 'Spanish',
    },
    foundingDate: '2024',
    areaServed: {
      '@type': 'Country',
      name: 'España',
      sameAs: 'https://www.wikidata.org/wiki/Q29',
    },
  };

  const softwareApp = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Bookoll',
    url: siteUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Bookoll es un software de reservas online para negocios de servicios. Permite gestionar citas, agenda, trabajadores, clientes y reservas desde una única plataforma.',
    featureList:
      'Reservas online 24/7, Gestión de agenda, Gestión de citas, Gestión de clientes, Panel de trabajadores, Recordatorios automáticos, Estadísticas de negocio, Página pública de reservas',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Reservas online gratuitas para clientes',
    },
    publisher: { '@id': `${siteUrl}/#organization` },
  };

  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Qué es Bookoll?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Bookoll es un software de reservas online que permite a cualquier negocio gestionar citas, agenda, clientes y trabajadores desde un único panel.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Para qué sirve Bookoll?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sirve para gestionar reservas online, agenda de citas, clientes y trabajadores en negocios de servicios.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Es gratis usar Bookoll?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, los clientes pueden reservar citas gratis y los negocios pueden empezar con un plan gratuito hasta diciembre de 2026.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Qué negocios pueden usar Bookoll?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Cualquier negocio que trabaje con citas o reservas: peluquerías, clínicas, entrenadores personales, academias, centros deportivos, estudios de tatuaje o servicios profesionales.',
        },
      },
    ],
  };

  return (
    <Helmet>
      <title>
        Bookoll — Software de reservas online y gestión de citas para negocios
      </title>
      <meta
        name="description"
        content="Software de reservas online para negocios de servicios. Gestiona citas, agenda, clientes y trabajadores con Bookoll. Plataforma de reservas disponible 24/7."
      />
      <meta
        name="keywords"
        content="software reservas online, sistema reservas, gestion agenda citas, software citas online, plataforma reservas online, agenda citas negocios, reservas online negocios, software gestion citas"
      />
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <link rel="canonical" href={siteUrl} />
      {/* [12] Solo x-default */}
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Bookoll" />
      <meta property="og:locale" content="es_ES" />
      <meta
        property="og:title"
        content="Bookoll — Software de reservas online y gestión de citas"
      />
      <meta
        property="og:description"
        content="Gestiona reservas, agenda, clientes y trabajadores con Bookoll. Plataforma de reservas online para negocios."
      />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:image" content={`${siteUrl}/og-home.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Bookoll software de reservas online"
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bookoll" />
      <meta
        name="twitter:title"
        content="Bookoll — Software de reservas online"
      />
      <meta
        name="twitter:description"
        content="Plataforma de reservas online para gestionar citas, agenda y clientes."
      />
      <meta name="twitter:image" content={`${siteUrl}/og-home.jpg`} />
      <meta
        name="twitter:image:alt"
        content="Bookoll software de reservas online"
      />

      <script type="application/ld+json">{JSON.stringify(website)}</script>
      <script type="application/ld+json">{JSON.stringify(organization)}</script>
      <script type="application/ld+json">{JSON.stringify(softwareApp)}</script>
      <script type="application/ld+json">{JSON.stringify(faq)}</script>
    </Helmet>
  );
}
